import axios from 'axios';
import { MNSproCloudApi } from '..';
import { Paginated } from '../../utils/paginated';
import { AbsenceResponse, LessonResponse } from '../api/v2';
import { LessonThemeFilterType, MissingQuantity, PaginatedOfLessonResponse, UnsignedFilterType } from '../api/v2/api';

const getClassRegisterApi = (authToken: string) => {
  const config = MNSproCloudApi.V2.createConfiguration(authToken);
  return MNSproCloudApi.V2.ClassRegisterApiFactory(config);
};

const validationOptions = {
  validateStatus: (status: number) => {
    return (status >= 200 && status <= 299) || status === 404;
  },
};

export const LessonService = {
  /**
   * Returns the current or next lesson taking place
   * for the given course.
   *
   * If the a lesson is currently taking place, it will be returned.
   * otherwise the next occurring lesson will be returned.
   *
   * If neither a current or next lesson was found, null will be returned.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} courseId
   * @return {*}  {Promise<LessonResponse>}
   * @memberof LessonService
   */
  async getCurrentOrNextLesson(
    authToken: string,
    organizationId: string,
    courseId: string,
  ): Promise<LessonResponse | null> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getLessonOfCourseNext(
      courseId,
      organizationId,
      undefined,
      undefined,
      undefined,
      validationOptions,
    );

    if (result.status === 404) {
      return null;
    }

    return result.data;
  },

  /**
   * Returns the lesson of the given course before
   * the given "startOfCurrentLesson" date.
   *
   * If no previous lesson was found, null will be returned.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} courseId
   * @param {string} startOfCurrentLesson
   * @return {*}  {Promise<LessonResponse>}
   * @memberof LessonService
   */
  async getPreviousLesson(
    authToken: string,
    organizationId: string,
    courseId: string,
    startOfCurrentLesson: string,
  ): Promise<LessonResponse | null> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getLessonOfCoursePrevious(
      courseId,
      organizationId,
      startOfCurrentLesson,
      undefined,
      undefined,
      validationOptions,
    );

    if (result.status === 404) {
      return null;
    }

    return result.data;
  },

  /**
   * Returns the lesson of the given course after
   * the provided "endOfCurrentLesson" date.
   *
   * If no next lesson is available, null will be returned.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} courseId
   * @param {string} endOfCurrentLesson
   * @return {*}  {Promise<LessonResponse>}
   * @memberof LessonService
   */
  async getNextLesson(
    authToken: string,
    organizationId: string,
    courseId: string,
    endOfCurrentLesson: string,
  ): Promise<LessonResponse | null> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getLessonOfCourseNext(
      courseId,
      organizationId,
      endOfCurrentLesson,
      undefined,
      undefined,
      validationOptions,
    );

    if (result.status === 404) {
      return null;
    }

    return result.data;
  },

  /**
   * Returns the count of Missing Themes and Signoffs for lessons
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @return {*}  {Promise<LessonToDoSummary>}
   * @memberof LessonService
   */
  async GetMissingLessonTaskSummaries(
    authToken: string,
    organizationId: string,
  ): Promise<MissingQuantity | null> {
    const api = getClassRegisterApi(authToken);

    const result = await api.getMissingInformationsCount(organizationId, undefined, {
      validateStatus: (status: number) => {
        return (status >= 200 && status <= 299) || status === 404;
      },
    });

    if (result.status === 404) {
      // 404 will be returned if for example the requesting user is not member
      // of the requested organization id.
      return null;
    }

    return result.data;
  },

  /**
   * Returns the lessons without a theme
   *
   * If no lesson is available, a empty array should be returned.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @return {*}  {Promise<LessonToDoSummary>}
   * @memberof LessonService
   */
  async GetLessonWithoutTheme(
    authToken: string,
    organizationId: string,
  ): Promise<Paginated<LessonResponse> | null> {
    const api = getClassRegisterApi(authToken);

    const result = await api.getLessonWithoutTheme(
      organizationId,
      undefined, // timePeriod
      undefined, // cont token
      undefined, // maxSize, set to undefined to enforce paging
      30,
    );

    const { value, nextLink, totalCount } = result.data;
    return {
      value,
      nextLink: nextLink === null ? undefined : nextLink,
      totalCount,
    };
  },

  /**
   * Returns the lessons without a theme
   *
   * If no lesson is available, a empty array should be returned.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @return {*}  {Promise<LessonToDoSummary>}
   * @memberof LessonService
   */
  async GetLessonWithoutThemeNextLink(
    authToken: string,
    organizationId: string,
    continuationToken: string,
  ): Promise<Paginated<LessonResponse> | null> {
    const result = await axios
      .create({
        headers: { Authorization: 'Bearer ' + authToken },
        baseURL: '',
      })
      .get(continuationToken);

    const { value, nextLink, totalCount } = result.data;
    return {
      value,
      nextLink: nextLink === null ? undefined : nextLink,
      totalCount,
    };
  },

  /**
   * Returns the lessons which are not signed off
   *
   * If no lesson is available, a empty array should be returned.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @return {*}  {Promise<LessonToDoSummary>}
   * @memberof LessonService
   */
  async getLessonWithMissingSignOffs(
    authToken: string,
    organizationId: string,
    groupId: string | null,
    userId: string | null,
    from: string,
    to: string,
    filterType: UnsignedFilterType,
    filterLessonTheme: LessonThemeFilterType = LessonThemeFilterType.none,
    pageSize: number = 20,
  ): Promise<Paginated<LessonResponse>> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getNoSignedLessonsByUserAndOrGroup(
      organizationId,
      undefined,
      groupId ?? undefined,
      userId ?? undefined,
      from,
      to,
      filterType,
      filterLessonTheme,
      undefined,
      undefined,
      pageSize,
      {
        validateStatus: (status: number) => {
          return (status >= 200 && status <= 299) || status === 404;
        },
      },
    );

    if (result.status === 404) {
      return {
        value: [],
      };
    }

    const { value, nextLink, totalCount } = result.data;

    return {
      value,
      nextLink: nextLink === null ? undefined : nextLink,
      totalCount,
    };
  },

  /**
   * Returns the lessons which are not signed off
   *
   * If no lesson is available, a empty array should be returned.
   *
   */
  async getLessonWithMissingSignOffsNextLink(
    authToken: string,
    continuationToken: string,
  ): Promise<Paginated<LessonResponse>> {
    const result = await axios
      .create({
        headers: { Authorization: 'Bearer ' + authToken },
        baseURL: '',
      })
      .get(continuationToken);

    const { value, nextLink, totalCount } = result.data;

    return {
      value,
      nextLink: nextLink === null ? undefined : nextLink,
      totalCount,
    };
  },

  /**
   * Returns lessons for a specified date range.
   * Optionally a max items number, page size and continuation token
   * can be provided for pagination.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} courseId
   * @param {string} start
   * @param {string} end
   * @param {string} [continuationToken] Should be provided from a previous call.
   * @param {number} [maxSize] Defines how much items should be returned in total.
   * @param {number} [pageSize] Defines how much items should be returned at a time.
   * @return {*}  {Promise<Paginated<LessonResponse>>}
   * @memberof LessonService
   */
  async getLessonsByRange(
    authToken: string,
    organizationId: string,
    courseId: string,
    start: string,
    end: string,
    continuationToken?: string,
    maxSize?: number,
    pageSize?: number,
  ): Promise<Paginated<LessonResponse> | null> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getLessonsOfCourseRange(
      courseId,
      end,
      organizationId,
      start,
      undefined,
      continuationToken,
      maxSize,
      pageSize,
      validationOptions,
    );

    if (result.status === 404) {
      return null;
    }
    let nextLink = result.data.nextLink;
    const { value, totalCount } = result.data;
    let lessonResponses: LessonResponse[] = value;
    while (nextLink) {
      const pageResponse = await axios.get<PaginatedOfLessonResponse>(nextLink, {
        headers: {
          authorization: 'Bearer ' + authToken,
        },
      });
      lessonResponses = lessonResponses.concat(pageResponse.data.value);
      nextLink = pageResponse.data.nextLink;
    }

    return {
      value: lessonResponses,
      totalCount: totalCount ?? 0,
    };
  },

  /**
   * Returns a specific lesson by id.
   * If no lesson was found, null will be returned.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} courseId
   * @param {string} lessonId
   * @return {*}  {(Promise<LessonResponse | null>)}
   * @memberof LessonService
   */
  async getLessonById(
    authToken: string,
    organizationId: string,
    courseId: string,
    lessonId: string,
  ): Promise<LessonResponse | null> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getLatestLesson(
      lessonId,
      organizationId,
      undefined,
      undefined,
      validationOptions,
    );

    if (result.data.course.id !== courseId) {
      // Can be removed when backend has implemented this check.
      // Needs do be done to avoid that a user can get a lesson of a different course.
      return null;
    }

    return result.data;
  },

  /**
   * Returns all submitted absence drafts whose time window overlaps the
   * given lesson (including its multi-lessons) for the lesson's students.
   *
   * Drafts that are already accepted are excluded by the backend.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} lessonId
   * @returns {Promise<AbsenceResponse[]>}
   * @memberof LessonService
   */
  async getLessonDraftAbsences(
    authToken: string,
    organizationId: string,
    lessonId: string,
  ): Promise<AbsenceResponse[]> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getLessonDraftAbsences(
      lessonId,
      organizationId,
      undefined,
      validationOptions,
    );

    if (result.status === 404) {
      return [];
    }

    return result.data;
  },
};
